import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { API_BASE } from '../config/environment';
import { apiGet, apiPost, apiDelete, setAuthContext } from '../utils/api';
import { useAuth } from './AuthContext';
import {
  loadTrainingDefaults,
  fetchTrainingDefaults,
  DEFAULT_TRAINING_DEFAULTS,
} from '../config/trainingDefaults';
import { mockSites, mockCapabilities, mockCapabilitiesForSite } from '../data/mockSites';

// Phase F1: site-based selection runs against mock fixtures until backend
// ships GET /sites and GET /sites/{id}/capabilities. Default ON until then.
const SITES_USE_MOCK =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITES_USE_MOCK) !== 'false';

// Create App Context
const AppContext = createContext();

// Custom hook to use the App Context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

const normalizeTrainingStatus = (rawStatus) => {
  if (!rawStatus) return 'idle';

  const upper = String(rawStatus).toUpperCase();

  if (upper === 'RUNNING' || upper === 'PENDING' || upper === 'UNKNOWN') {
    return 'running';
  }

  if (upper === 'PAUSED') {
    return 'paused';
  }

  if (upper === 'SUCCEEDED' || upper === 'COMPLETED') {
    return 'completed';
  }

  if (upper === 'FAILED' || upper === 'ERROR') {
    return 'error';
  }

  return String(rawStatus).toLowerCase();
};

// App Context Provider Component
export const AppProvider = ({ children }) => {
  // Get auth context for API calls
  const auth = useAuth();
  
  // Set auth context for API utility
  useEffect(() => {
    // Set auth context for API utility
    setAuthContext(auth);
  }, [auth]);
  // Shared state
  const [models, setModels] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [trainingStatus, setTrainingStatus] = useState(null);
  const [trainingProgress, setTrainingProgress] = useState(null);
  const [config, setConfig] = useState(null);
  const [currentModel, setCurrentModel] = useState('');
  const [currentConfig, setCurrentConfig] = useState(null); // Add currentConfig state

  // Per-user training defaults (Settings → Federated Training). Seeded from the
  // localStorage cache for instant paint, then reconciled with the server on
  // login via fetchTrainingDefaultsFromServer(). The Settings and Configuration
  // screens read these so a saved config follows the user across logins/devices.
  const [trainingDefaults, setTrainingDefaults] = useState(() => loadTrainingDefaults());
  
  // New state for tracking execution history
  const [executionHistory, setExecutionHistory] = useState([]);
  
  // Training data from database
  const [trainingRuns, setTrainingRuns] = useState([]);
  const [trainingSummary, setTrainingSummary] = useState(null);
  const [trainingDataLoading, setTrainingDataLoading] = useState(false);
  
  // Available options for config editing
  const [availableModels, setAvailableModels] = useState([]);
  const [availableDatasets, setAvailableDatasets] = useState([]);
  const [displayableFeatures, setDisplayableFeatures] = useState([]);
  const [featureMetadata, setFeatureMetadata] = useState({});
  const [displayableFeaturesLoading, setDisplayableFeaturesLoading] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [featureVisualizations, setFeatureVisualizations] = useState(null);
  
  // Segmentation-specific states
  const [segmentationStatus, setSegmentationStatus] = useState(null);
  const [segmentationProgress, setSegmentationProgress] = useState(null);
  const [segmentationVisualizations, setSegmentationVisualizations] = useState([]);
  const [segmentationHistory, setSegmentationHistory] = useState([]);
  const [currentSegmentationRun, setCurrentSegmentationRun] = useState(null);
  
  // Loading states
  const [modelsLoading, setModelsLoading] = useState(false);
  const [datasetsLoading, setDatasetsLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [progressLoading, setProgressLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [executionHistoryLoading, setExecutionHistoryLoading] = useState(true); // Initial load
  const [isCompleted, setIsCompleted] = useState(() => {
    try {
      return localStorage.getItem('trainingStatusCompleted') === 'true';
    } catch (e) {
      return false;
    }
  });
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  
  // Segmentation loading states
  const [segmentationLoading, setSegmentationLoading] = useState(false);
  const [segmentationVisualizationsLoading, setSegmentationVisualizationsLoading] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  // Execute page specific states (moved from Execute component for shared access)
  const [progress, setProgress] = useState(0);
  const [currentRound, setCurrentRound] = useState(0);
  const [totalRounds, setTotalRounds] = useState(3);
  const [connectedClients, setConnectedClients] = useState(0);
  const [visualizationsGenerated, setVisualizationsGenerated] = useState(false);
  const [hasBeenCleared, setHasBeenCleared] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState(null);
  const [isGeneratingVisualizations, setIsGeneratingVisualizations] = useState(false);
  const [logs, setLogs] = useState(() => {
    // Load logs from localStorage on initialization
    try {
      const savedLogs = localStorage.getItem('executeLogs');
      return savedLogs ? JSON.parse(savedLogs) : [];
    } catch (error) {
      console.error('Failed to load logs from localStorage:', error);
      return [];
    }
  });
  const [trainingResults, setTrainingResults] = useState(() => {
    try {
      const savedResults = localStorage.getItem('trainingResults');
      return savedResults ? JSON.parse(savedResults) : null;
    } catch (e) {
      console.error('Failed to load trainingResults from localStorage:', e);
      return null;
    }
  });

  // Error states
  const [errors, setErrors] = useState({});

  // Feature map for multiple datasets
  const [perDatasetFeaturesMap, setPerDatasetFeaturesMap] = useState({});

  // --- Phase F1: site-based dataset selection state ---
  // `sites` and `siteCapabilities` are populated from mock fixtures by default.
  // `selectedSites` is the user's current picker state; the configs scene
  // merges this into the run-config payload as `sites: [{site_id, capability_id}]`.
  const [sites, setSites] = useState([]);
  const [siteCapabilities, setSiteCapabilities] = useState({});  // { [site_id]: SiteDatasetCapability[] }
  const [selectedSites, setSelectedSites] = useState([]);         // { site_id, capability_id }[]
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState(null);

  // --- Phase F1.x prototype: schema validation state ---
  // Stores the most recent mock validation result with a signature of the
  // selection it ran against. UI components compare the current signature
  // (computed from selectedSites + processingType) against `signature` here
  // to detect "stale" pre-check results. `source` distinguishes the two
  // trigger points: "pre_check" (optional, configs page) vs "final"
  // (mandatory gate on the execute page Start button).
  const [schemaPreCheck, setSchemaPreCheck] = useState({
    signature: null,
    result: null,    // null | { passed, code, summary, expectedSchema, siteResults, source, ranAt }
    status: "idle",  // "idle" | "checking"
  });

  // Helper function to handle API errors
  const handleApiError = (key, error) => {
    console.error(`Error fetching ${key}:`, error);
    setErrors(prev => ({ ...prev, [key]: error.message }));
  };

  // Clear specific error
  const clearError = (key) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[key];
      return newErrors;
    });
  };

  // Fetch models data
  const fetchModels = async () => {
    if (modelsLoading) return; // Prevent duplicate calls
    
    setModelsLoading(true);
    clearError('models');
    
    try {
      const data = await apiGet('/models');
      // Backend returns models directly as an array, not wrapped in data.models
      const modelsArray = Array.isArray(data) ? data : (data.models || []);
      setModels(modelsArray);
      setCurrentModel(data.current_model || '');
      // Also update available models for config editing
      setAvailableModels(modelsArray);
    } catch (error) {
      handleApiError('models', error);
    } finally {
      setModelsLoading(false);
    }
  };

  // Fetch datasets data
  const fetchDatasets = async () => {
    if (datasetsLoading) return; // Prevent duplicate calls

    setDatasetsLoading(true);
    clearError('datasets');

    try {
      // F1.7 prototype path: derive /datasets from mockCapabilities so the
      // page expresses the site-local product model — every dataset belongs
      // to an owning site. Real GET /datasets is NOT called in mock mode.
      // `datasets` carries capability_id strings (existing UI contract: array
      // of opaque path/id tokens). `availableDatasets` carries the full
      // SiteDatasetCapability objects so the Datasets scene can look up
      // owning site / readiness / access for each card.
      if (SITES_USE_MOCK) {
        const capIds = mockCapabilities.map((c) => c.capability_id);
        setDatasets(capIds);
        setAvailableDatasets(mockCapabilities);
        return;
      }

      // F1.8 — AKS-real path: GET /api/datasets is NOT exposed by AKS DEV
      // (dev-0.7.x). The source of truth for "what logical datasets exist"
      // is the per-site /sites/{id}/capabilities response which fetchSites()
      // real branch already loaded and wrote into availableDatasets. This
      // path is intentionally a no-op in real mode to avoid a useless 404.
      // refreshDatasets() and fetchAllData() callers still resolve cleanly.
      return;
    } catch (error) {
      handleApiError('datasets', error);
    } finally {
      setDatasetsLoading(false);
    }
  };

  // Track the last dataset path to prevent duplicate calls
  const lastDatasetPathRef = useRef(null);
  const flowerTaskIdRef = useRef(null);
  
  // Cache for displayable features
  const featureCacheRef = useRef(new Map());
  const featurePromisCacheRef = useRef(new Map());
  // Remember dataset paths that already failed so we don't retry them in a
  // loop. Without this, a 500 (e.g. missing dataset file) is never cached and
  // every re-render re-fires the request, hammering the backend.
  const featureErrorCacheRef = useRef(new Map());

  // Clear feature cache
  const clearFeatureCache = () => {
    featureCacheRef.current.clear();
    featureErrorCacheRef.current.clear();
  };
  
  // Fetch displayable features for tabular data visualization
  const fetchDisplayableFeatures = async (datasetPath = null) => {
    // Use provided dataset path or current config dataset path
    const targetDatasetPath = datasetPath || config?.dataset_config?.dataset_path || "feature_files/available_datasets/dataset_primary_site.csv";
    
    // Check Cache First
    if (featureCacheRef.current.has(targetDatasetPath)) {
        const cachedData = featureCacheRef.current.get(targetDatasetPath);
        setDisplayableFeatures(cachedData.features);
        setFeatureMetadata(cachedData.metadata || {});
        // Ensure loading is false (might be true if switching quickly)
        setDisplayableFeaturesLoading(false);
        return cachedData.features;
    }

    // Don't retry a path we already know is failing. Returning early here
    // (without setting new state references) breaks the render -> fetch ->
    // setState -> render retry loop for missing/broken datasets.
    if (featureErrorCacheRef.current.has(targetDatasetPath)) {
        setDisplayableFeaturesLoading(false);
        return [];
    }

    // Prevent duplicate calls using Promise cache
    if (featurePromisCacheRef.current.has(targetDatasetPath)) {
        return featurePromisCacheRef.current.get(targetDatasetPath);
    }

    // Update the last dataset path and set loading
    lastDatasetPathRef.current = targetDatasetPath;
    setDisplayableFeaturesLoading(true);
    
    const fetchPromise = (async () => {
        try {
          const data = await apiPost('/displayable-features', {
            dataset_path: targetDatasetPath
          });
          
          const features = data.displayable_features || [];
          const metadata = data.feature_metadata || {};
          
          // Update Cache
          featureCacheRef.current.set(targetDatasetPath, { features, metadata });
          
          setDisplayableFeatures(features);
          setFeatureMetadata(metadata);
          return features;
        } catch (error) {
          console.error('[AppContext] Error fetching displayable features:', error);
          // Record the failure so subsequent renders short-circuit instead of
          // retrying this path indefinitely.
          featureErrorCacheRef.current.set(targetDatasetPath, error.message);
          setErrors(prev => ({ ...prev, displayableFeatures: error.message }));
          setDisplayableFeatures([]);
          setFeatureMetadata({});
          return [];
        } finally {
          featurePromisCacheRef.current.delete(targetDatasetPath);
          setDisplayableFeaturesLoading(false);
        }
    })();
    
    featurePromisCacheRef.current.set(targetDatasetPath, fetchPromise);
    return fetchPromise;
  };

  // Generate feature visualizations
  // Generate feature visualizations
  const generateFeatureVisualizations = useCallback(async (selectedFeatures, datasetPathOrPaths) => {
    try {
      const datasetPaths = Array.isArray(datasetPathOrPaths) ? datasetPathOrPaths : [datasetPathOrPaths];
      const allVisualizations = [];
      let totalSamples = 0;

      for (const datasetPath of datasetPaths) {
          if (!datasetPath) continue;
          const data = await apiPost('/feature_visualizations', {
            selected_features: selectedFeatures,
            dataset_path: datasetPath
          });
          
          if (data && data.visualizations) {
             data.visualizations.forEach(viz => {
                 viz.dataset_path = datasetPath;
             });
             allVisualizations.push(...data.visualizations);
             if (data.num_sample) {
                 totalSamples += data.num_sample; // Roughly sum samples across fetched datasets
             }
          }
      }
      
      // Merge visualizations by feature name
      const mergedByFeature = {};
      allVisualizations.forEach(viz => {
          if (!mergedByFeature[viz.feature_name]) {
              mergedByFeature[viz.feature_name] = {
                  feature_name: viz.feature_name,
                  chart_type: viz.chart_type,
                  datasets: []
              };
          }
          mergedByFeature[viz.feature_name].datasets.push({
              dataset_path: viz.dataset_path,
              data: viz.data
          });
      });
      
      const finalVisualizations = Object.values(mergedByFeature);
      
      const combinedData = {
          visualizations: finalVisualizations,
          num_feature: finalVisualizations.length,
          num_sample: totalSamples
      };
      
      setFeatureVisualizations(combinedData);
      return combinedData;
    } catch (error) {
      console.error('Error generating feature visualizations:', error);
      handleApiError('featureVisualizations', error);
      throw error;
    }
  }, []);

  // Fetch training runs from database
  const fetchTrainingRuns = async (limit = 10, offset = 0) => {
    setTrainingDataLoading(true);
    try {
      const data = await apiGet(`/user/training-runs?limit=${limit}&offset=${offset}`);
      if (data.success) {
        setTrainingRuns(data.training_runs);
        return data.training_runs;
      }
    } catch (error) {
      console.error('Error fetching training runs:', error);
    } finally {
      setTrainingDataLoading(false);
    }
  };

  // Fetch training summary statistics
  const fetchTrainingSummary = useCallback(async (background = false) => {
    if (!background) setExecutionHistoryLoading(true);
    try {

      // Suppress auth error to allow dashboard to load (empty) even if user doesn't exist in DB yet
      const data = await apiGet('/user/training-summary', { suppressAuthError: true });
      console.log('[DEBUG] fetchTrainingSummary response:', data);
      if (data.success) {
        setTrainingSummary(data.summary);
        // Update execution history with recent runs
        if (data.recent_runs && data.recent_runs.length > 0) {
          console.log('[TIMESTAMP_CHECK] Latest run timestamp from backend:', data.recent_runs[0].start_time);
          const formattedHistory = data.recent_runs.map(run => ({
            id: `run-${run.id}`,
            timestamp: run.start_time,
            modelType: run.model_type,
            datasetPath: run.dataset_path,
            datasetName: run.dataset_name,
            rounds: run.total_rounds,
            total_rounds: run.total_rounds,
            total_rounds_completed: run.total_rounds_completed,
            completed: run.status === 'completed',
            accuracy: run.final_accuracy ? parseFloat(run.final_accuracy) : 0,
            loss: run.final_loss ? parseFloat(run.final_loss) : 0,
            // Seg primary metric. Pull all three candidate keys backend might
            // surface (depends on release). Use `undefined` (not 0) when missing
            // so the Overview Dice tile renders 'N/A' instead of a misleading 0%.
            final_dice_hard: run.final_dice_hard != null ? parseFloat(run.final_dice_hard) : undefined,
            final_dice: run.final_dice != null ? parseFloat(run.final_dice) : undefined,
            dice_hard: run.dice_hard != null ? parseFloat(run.dice_hard) : undefined,
            duration: run.duration_seconds,
            status: run.status,
            trainingRunId: run.id,
            // dev-0.7.46 — backend now exposes flower_task_id + processing_type
            // in /api/db/training-runs SELECT. AllCompletedRunsModal's Execution
            // tab uses flower_task_id to embed the Tier1 result card iframe
            // for completed runs (null for pre-Tier1 historic rows → falls back
            // to legacy loss/accuracy charts).
            flower_task_id: run.flower_task_id || null,
            processing_type: run.processing_type || null,
            config_data: run.config_data, // Critical for Use Configuration
            // Pass through flat config fields if they exist from DB
            num_server_rounds: run.num_server_rounds,
            fraction_evaluate: run.fraction_evaluate,
            local_epochs: run.local_epochs,
            learning_rate: run.learning_rate,
            batch_size: run.batch_size,
            partitioning_method: run.partitioning_method,
            trainingResults: run.training_results || {} // Add trainingResults for dashboard graph
          }));
          setExecutionHistory(formattedHistory);

          // ── Enrich with config_data via per-row detail fetch ───────────────
          // The slim /api/user/training-summary response intentionally omits
          // `config_data` (and `selected_datasets`) — same gap that hit F8b
          // for `flower_task_id`. Without those fields the dataset-name
          // resolver falls back to the literal backend-default `dataset_path`
          // (e.g. "dataset_primary_site_c1.csv") for every row.
          //
          // Fix: fan out per-row /api/user/training-runs/<id> in parallel,
          // merge `config_data` into each row, then re-set executionHistory.
          // Non-blocking; the friendly name appears as soon as enrichment
          // resolves. Cheap because list length is bounded.
          (async () => {
            // Cap to the most recent N rows. The list can grow to 100s of
            // historic rows; firing one detail fetch per row immediately
            // saturates the browser's per-origin connection pool (~6) and
            // the queue tail can take minutes to drain. We only need
            // enrichment for rows the user will actually see in the modal
            // (sorted newest-first, top ~50 covers visible scroll). Older
            // rows still render via the model-name-only inference fallback
            // in resolveDatasetDisplayNameForExecution.
            const ENRICH_LIMIT = 50;
            try {
              const headRows = formattedHistory.slice(0, ENRICH_LIMIT);
              const tailRows = formattedHistory.slice(ENRICH_LIMIT);
              const enrichedHead = await Promise.all(
                headRows.map(async (row) => {
                  if (row.config_data && typeof row.config_data === 'object') {
                    return row;
                  }
                  const rid = row.trainingRunId;
                  if (rid === undefined || rid === null) return row;
                  try {
                    const detail = await apiGet(
                      `/user/training-runs/${rid}`,
                      { suppressAuthError: true }
                    );
                    const tr = detail?.training_run || detail || null;
                    if (!tr) return row;
                    return {
                      ...row,
                      config_data: tr.config_data || row.config_data || null,
                      flower_task_id: row.flower_task_id || tr.flower_task_id || null,
                      processing_type: row.processing_type || tr.processing_type || null,
                      // dev-0.7.50: additional detail-only fields the
                      // Execution Details Configuration tab consumes
                      // (Best metrics, Failure section, Ended timestamp).
                      // Slim summary list doesn't carry these — only detail
                      // endpoint does. `?? undefined` (not `?? 0`) so the FE
                      // can distinguish "not yet computed" from "actually 0".
                      best_accuracy: tr.best_accuracy != null ? Number(tr.best_accuracy) : undefined,
                      best_loss: tr.best_loss != null ? Number(tr.best_loss) : undefined,
                      error_message: tr.error_message || null,
                      end_time: tr.end_time || null,
                    };
                  } catch (_) {
                    return row;
                  }
                })
              );
              setExecutionHistory([...enrichedHead, ...tailRows]);
            } catch (e) {
              console.warn('[AppContext] executionHistory enrichment failed:', e?.message || e);
            }
          })();
        }
        return data;
      } else {
      }
    } catch (error) {
      console.error('Error fetching training summary:', error);
    } finally {
      if (!background) setExecutionHistoryLoading(false);
    }
  }, []); // Empty dependency array since this function doesn't depend on any state

  // Fetch training metrics for a specific run
  const fetchTrainingMetrics = useCallback(async (trainingRunId) => {
    try {
      // Use suppressAuthError to prevent 401s from triggering global session expiry
      // This is particularly important because looking up metrics for runs that don't belong 
      // to the user (or failing permissions) shouldn't logout the user
      const data = await apiGet(`/user/training-runs/${trainingRunId}/metrics`, { suppressAuthError: true });
      if (data.success) {
        return data.metrics;
      }
    } catch (error) {
      console.error('Error fetching training metrics:', error);
    }
  }, []);

  // Clear cached data from backend and reset current training state (preserve historical data)
  const clearCachedData = async () => {
    try {
      //TODO add manual clearing process of data cache
      // await apiPost('/clear_cache');
      // console.log('Backend cache cleared successfully');
      
      // Reset only current training state, preserve executionHistory for dashboard
      setTrainingStatus(null);
      setTrainingProgress(null);
      setFeatureVisualizations(null);
      // DON'T clear executionHistory - this contains database training results for dashboard
    } catch (error) {
      console.error('Error clearing backend cache:', error);
    }
  };

  // Clear all training data including segmentation cache and Execute page states
  const clearAllTrainingData = async () => {
    setIsClearing(true);
    try {
      if (window.clearSegmentationCache) {
        window.clearSegmentationCache();
      }
      
      // Clear backend training status
      try {
        await apiPost('/clear_training_status');
      } catch (backendError) {
        console.warn('[AppContext] Backend clear failed (endpoint may not exist):', backendError.message);
        // Continue even if backend clear fails
      }
      
      // Clear feature cache
      clearFeatureCache();

      // Clear regular training data
      await clearCachedData();
      
      // Reset segmentation states
      setSegmentationStatus(null);
      setSegmentationProgress(null);
      setSegmentationVisualizations([]);
      setCurrentSegmentationRun(null);
      
      // Reset training states
      setIsCompleted(false);
      setIsRunning(false);
      setIsPaused(false);
      
      // Reset Execute page specific states
      setLogs([]);
      setTrainingResults(null);
      setVisualizationsGenerated(false);
      setHasBeenCleared(true);
      setProgress(0);
      setCurrentRound(0);
      setConnectedClients(0);
      setError(null);
      setIsGeneratingVisualizations(false);
      setIsPolling(false);
      
      // Clear localStorage items used by Execute component
      try {
        localStorage.removeItem('executeLogs');
        localStorage.removeItem('executeCompletionLogged');
        localStorage.removeItem('trainingResults');
        localStorage.removeItem('selectedFeatures');
        localStorage.removeItem('featureVisualizations');
      } catch (storageError) {
        console.warn('[AppContext] Could not clear localStorage:', storageError);
      }
      

    } catch (error) {
      console.error('[AppContext] Error clearing all training data:', error);
    } finally {
      setIsClearing(false);
    }
  };
  
    // Clears all training run history from the database
  const clearTrainingRunHistory = async () => {
    try {
        await apiDelete('/user/training-runs'); 
        setExecutionHistory([]);
        await fetchTrainingSummary();
    } catch (error) {
        console.error("Failed to clear run history:", error);
    }
  };

  // Check if training is completed
  const isTrainingCompleted = (status, progress) => {
    return status?.status === 'completed' || progress?.progress?.percentage >= 100;
  };

  // Fetch training status
  const fetchTrainingStatus = async () => {
    if (statusLoading) return; // Prevent duplicate calls
    
    setStatusLoading(true);
    clearError('status');
    
    try {
      const data = await apiGet('/status');

      // Backend returns training_status object directly, so use data.status or data.server
      const rawStatus = data.status || data.server || 'idle';
      const status = normalizeTrainingStatus(rawStatus);
      // Merge with previous trainingStatus instead of overwriting. /api/status
      // doesn't return `flower_task` (that field is set separately by
      // fetchFlowerTaskStatus during active-task polling), so a plain replace
      // wipes the just-completed run's task_id + tier1_summary on Execute
      // remount — which makes the result-card viewer fall back to the mock
      // iframe URL even though the real Tier1 HTML is fetchable. New /api/status
      // fields (including the freshly-normalized status) still take precedence.
      setTrainingStatus(prev => ({ ...(prev || {}), status, rawStatus, ...data }));
      
      // CRITICAL: Update segmentationProgress if this is segmentation training
      if (data.training_type === 'segmentation') {
        setSegmentationProgress({
          percentage: data.progress || 0,
          current_epoch: data.current_round || 0,
          total_epochs: data.total_rounds || 1,
          message: data.message
        });
        setSegmentationStatus(status);
      }
      
      // CRITICAL: Sync isRunning, isPaused, isCompleted based on actual backend status
      // This ensures UI reflects correct state when navigating back to Execute page
      if (status === 'running') {
        setIsRunning(true);
        setIsPaused(false);
        setIsCompleted(false);
      } else if (status === 'paused') {
        setIsRunning(false);
        setIsPaused(true);
        setIsCompleted(false);
      } else if (status === 'completed') {
        setIsRunning(false);
        setIsPaused(false);
        setIsCompleted(true);
      } else {
        // idle or other status — backend has no active task right now.
        // Do NOT downgrade isCompleted here: on navigate-away-and-back the
        // Execute page re-fires this fetch and Flask /api/status legitimately
        // returns 'idle' for a just-completed run (the active-task slot has
        // been released). Clearing isCompleted in that case wipes the result
        // card even though trainingStatus.flower_task.task_id + tier1_summary
        // are still in AppContext. The "next run starts" reset is owned by
        // executeTraining (setIsCompleted(false) at line 917) and the
        // explicit-clear reset is owned by handleClearAll.
        //
        // Same conservation rule applies to isRunning: Flask /api/status
        // doesn't track Flower-based (seg/cls) training — those route through
        // /api/execute_training_flower and are tracked by fetchFlowerTaskStatus
        // separately. So an 'idle' response on Execute remount mid-training
        // is a false signal. Resetting isRunning here makes TrainingAnimation
        // hide on remount and falsely suggests the run was interrupted.
        // fetchFlowerTaskStatus is authoritative for the running/completed
        // transition; executeTraining sets isRunning=true on start;
        // stopTraining/clearAllRunData set it false on explicit user action.
        setIsPaused(false);
      }
    } catch (error) {
      console.error('Status fetch error:', error);
      handleApiError('status', error);
    } finally {
      setStatusLoading(false);
    }
  };

  // Fetch training progress - restored from working version
  const fetchTrainingProgress = async () => {
    if (progressLoading) return; // Prevent duplicate calls
    
    setProgressLoading(true);
    clearError('progress');
    
    try {
      const data = await apiGet('/training_progress');
      const mappedStatus = normalizeTrainingStatus(data.status);
      const mappedData = { ...data, status: mappedStatus };
      setTrainingProgress(mappedData);
      
      // CRITICAL: Sync isRunning, isPaused, isCompleted based on progress status
      // This provides a second checkpoint to ensure UI state matches backend
      if (mappedStatus === 'running') {
        setIsRunning(true);
        setIsPaused(false);
        setIsCompleted(false);
      } else if (mappedStatus === 'paused') {
        setIsRunning(false);
        setIsPaused(true);
        setIsCompleted(false);
      } else if (mappedStatus === 'completed' || mappedData.progress?.percentage >= 100) {
        setIsRunning(false);
        setIsPaused(false);
        setIsCompleted(true);
      }
        
      // If training is completed, add to execution history
      if (mappedStatus === 'completed' || mappedData.progress?.percentage >= 100) {
          const timestamp = getCurrentUTCDate();
          const executionId = `exec-${timestamp}`;
          
          // Only add to history if it's a new execution or has updated results
          setExecutionHistory(prev => {
            // Check if we already have this execution in history
            const existingIndex = prev.findIndex(
              exec => exec.trainingResults && 
                     data.all_rounds && 
                     JSON.stringify(exec.trainingResults) === JSON.stringify(data.all_rounds)
            );
            
            if (existingIndex === -1) {
              // Add new execution to history
              return [{
                id: executionId,
                timestamp,
                modelType: currentConfig?.dataset_config?.model_type || 'Unknown',
                datasetPath: currentConfig?.dataset_config?.dataset_path || 'Unknown',
                rounds: currentConfig?.app_config?.["num-server-rounds"] || 0,
                completed: true,
                trainingResults: data.all_rounds || {},
                accuracy: data.latest_metrics?.round_metrics?.accuracy || 0,
                loss: data.latest_metrics?.round_metrics?.loss || 0,
                config: { ...currentConfig }  // Store full config for reference
              }, ...prev].slice(0, 10); // Keep only the 10 most recent executions
            }
            
            return prev;
          });
        } 
        // If training is in progress, update or add to execution history
        else if (mappedStatus === 'running' && mappedData.progress?.current_round > 0) {
          setExecutionHistory(prev => {
            // Try to find an in-progress execution
            const existingIndex = prev.findIndex(
              exec => !exec.completed && exec.rounds === currentConfig?.app_config?.["num-server-rounds"]
            );
            
            if (existingIndex === -1) {
              // Add new in-progress execution
              const timestamp = getCurrentUTCDate();
              const executionId = `exec-${timestamp}`;
              
              return [{
                id: executionId,
                timestamp,
                modelType: currentConfig?.dataset_config?.model_type || 'Unknown',
                datasetPath: currentConfig?.dataset_config?.dataset_path || 'Unknown',
                rounds: currentConfig?.app_config?.["num-server-rounds"] || 0,
                currentRound: data.progress?.current_round || 0,
                completed: false,
                trainingResults: data.all_rounds || {},
                accuracy: data.latest_metrics?.round_metrics?.accuracy || 0,
                loss: data.latest_metrics?.round_metrics?.loss || 0,
                config: { ...currentConfig }  // Store full config for reference
              }, ...prev].slice(0, 10); // Keep only the 10 most recent executions
            } else {
              // Update existing in-progress execution
              const updatedHistory = [...prev];
              updatedHistory[existingIndex] = {
                ...updatedHistory[existingIndex],
                currentRound: data.progress?.current_round || 0,
                trainingResults: data.all_rounds || {},
                accuracy: data.latest_metrics?.round_metrics?.accuracy || 0,
                loss: data.latest_metrics?.round_metrics?.loss || 0
              };
              
              return updatedHistory;
            }
          });
        }
    } catch (error) {
      handleApiError('progress', error);
    } finally {
      setProgressLoading(false);
      setConfigLoading(false);
    }
  };

  // Fetch configuration from backend
  // Pull the user's saved training defaults from the server (users.preferences)
  // and refresh both context state and the localStorage cache. Safe to call on
  // login; falls back to the cache when the backend is unreachable.
  const fetchTrainingDefaultsFromServer = async () => {
    try {
      const { training } = await fetchTrainingDefaults();
      setTrainingDefaults(training);
      return training;
    } catch (error) {
      console.warn('[AppContext] fetchTrainingDefaults failed:', error?.message || error);
      const cached = loadTrainingDefaults();
      setTrainingDefaults(cached);
      return cached;
    }
  };

  // Called by the Settings page after a successful save so other open screens
  // (Configuration) immediately see the new defaults without a remount.
  const applyTrainingDefaults = (values) => {
    setTrainingDefaults({ ...DEFAULT_TRAINING_DEFAULTS, ...values });
  };

  const fetchConfig = async () => {
    setConfigLoading(true);
    clearError('configFetch');
    
    try {
      const data = await apiGet('/config');

      // Try to enrich backend data that might be missing selected_datasets legacy compatibility
      let enrichedData = { ...data };
      if (!enrichedData.dataset_config?.selected_datasets || enrichedData.dataset_config.selected_datasets.length === 0) {
           try {
              const savedConfigStr = localStorage.getItem('savedConfig');
              if (savedConfigStr) {
                  const savedConfig = JSON.parse(savedConfigStr);
                  if (savedConfig?.dataset_config?.selected_datasets?.length > 0) {
                      if (!enrichedData.dataset_config) enrichedData.dataset_config = {};
                      enrichedData.dataset_config.selected_datasets = savedConfig.dataset_config.selected_datasets;
                  }
              }
           } catch(e) {}
      }

      setConfig(enrichedData);
      setCurrentConfig(enrichedData);

      
      // If backend config is empty/default, try to load from localStorage
      if (!enrichedData || Object.keys(enrichedData).length === 0 || !enrichedData.dataset_config?.dataset_path) {
        try {
          const savedConfigStr = localStorage.getItem('savedConfig');
          if (savedConfigStr) {
            const savedConfig = JSON.parse(savedConfigStr);
            
            // Remove metadata fields before using
            const { savedAt, version, ...configData } = savedConfig;
            
            // Update state with saved config
            setConfig(configData);
            setCurrentConfig(configData);
            
            // Optionally sync to backend
            try {
              await apiPost('/config', configData);
            } catch (syncError) {
              console.warn('[AppContext] Failed to sync config to backend:', syncError);
            }
          }
        } catch (storageError) {
          console.warn('[AppContext] Failed to load config from localStorage:', storageError);
        }
      }
    } catch (error) {
      handleApiError('configFetch', error);
      
      // If backend fetch fails, try localStorage as fallback
      // If backend fetch fails, try localStorage as fallback
      try {
        const savedConfigStr = localStorage.getItem('savedConfig');
        if (savedConfigStr) {
          const savedConfig = JSON.parse(savedConfigStr);
          
          const { savedAt, version, ...configData } = savedConfig;
          setConfig(configData);
          setCurrentConfig(configData);
        }
      } catch (storageError) {
        console.warn('[AppContext] Failed to load fallback config from localStorage:', storageError);
      }
    } finally {
      setConfigLoading(false);
    }
  };

  // Fetch selected features from backend
  const fetchSelectedFeatures = async () => {
    try {
      const data = await apiGet('/config/selected-features');
      setSelectedFeatures(data.selected_features || []);
      return data.selected_features || [];
    } catch (error) {
      console.error('Error fetching selected features:', error);
      return [];
    }
  };

  // Update selected features on backend
  const updateSelectedFeatures = async (features) => {
    try {
      const data = await apiPost('/config/selected-features', {
        selected_features: features
      });
      setSelectedFeatures(features);
      return { success: true, data };
    } catch (error) {
      console.error('Error updating selected features:', error);
      return { success: false, error: error.message };
    }
  };

  // Save configuration
  const saveConfig = async (configData) => {
    setConfigSaving(true);
    clearError('configSave');
    
    try {
      // Update configuration via API
      await apiPost('/config', configData);
      
      // Immediately update local state with the new config data
      setConfig(configData);
      setCurrentConfig(configData);
      
      // CRITICAL: Save config to localStorage for persistence
      try {
        const configToStore = {
          ...configData,
          savedAt: new Date().toISOString(),
          version: '1.0'
        };
        localStorage.setItem('savedConfig', JSON.stringify(configToStore));
      } catch (storageError) {
        console.warn('[AppContext] Failed to save config to localStorage:', storageError);
      }
      
      // Optionally save to file
      try {
        await apiPost('/config/save');
        
        // Completely reset training state when config is saved
        setTrainingStatus(null);
        setTrainingProgress({
          status: 'idle',
          progress: { percentage: 0, current_round: 0 },
          all_rounds: {},
          latest_metrics: {}
        });
        
        // dev-0.7.8 — DO NOT re-fetch config from backend after save.
        // The legacy flow used to `await fetchConfig()` here to "refresh
        // for validation", but on AKS DEV that GET /api/config returns
        // a different shape than what we POSTed (missing app_config,
        // dataset_config), and `setCurrentConfig(enrichedData)` ended up
        // OVERWRITING the user's just-saved values (in particular
        // num-server-rounds got reset to whatever GET returns, which is
        // 3 — the legacy default). User-reported symptom: set rounds=4
        // in UI, click Save, UI immediately reverts to "3 rounds".
        // We've already done `setCurrentConfig(configData)` above with
        // the exact payload we POSTed, so currentConfig is the source
        // of truth from this point on.
        // (If backend ever needs to enrich the local copy with
        // server-derived fields like execution_assignment, consume the
        // POST response — apiPost('/config', configData) — and merge
        // selectively here. Don't blindly re-fetch.)

        return { success: true, message: 'Configuration saved successfully! Training progress has been reset.' };
      } catch (saveError) {
        // Even if file save fails, we still have the config updated in memory and API
        return { success: true, message: 'Configuration updated but file save failed' };
      }
    } catch (error) {
      // Prototype / offline fallback — when the backend at :3001 is unreachable
      // (the typical Mock UI Prototype run on port 3010 alone), apiPost throws
      // with a native "Failed to fetch". We mirror the success-path local
      // updates so /execute reads consistent currentConfig, and we DON'T
      // surface the network error as a user-facing Alert — from the user's
      // point of view the save did succeed (config is in currentConfig +
      // localStorage). Log via console.warn so devs can still trace it.
      try {
        setConfig(configData);
        setCurrentConfig(configData);
        const configToStore = {
          ...configData,
          savedAt: new Date().toISOString(),
          version: '1.0',
        };
        localStorage.setItem('savedConfig', JSON.stringify(configToStore));
      } catch (localErr) {
        console.warn('[AppContext] Offline saveConfig local update failed:', localErr);
      }
      console.warn(
        '[AppContext] saveConfig backend unreachable, saved locally:',
        error?.message || error
      );
      // Clear any prior configSave error so /configs doesn't keep showing
      // a stale "Failed to fetch" Alert from an earlier attempt.
      clearError('configSave');
      return {
        success: true,
        offline: true,
        message: 'Configuration saved locally (backend unreachable).',
      };
    } finally {
      setConfigSaving(false);
    }
  };

  // Training execution functions - restored from working version
  const executeTraining = async () => {
    setIsRunning(true);
    setIsPaused(false);
    setIsCompleted(false);
    // dev-0.7.16: also clear the "post-Clear" sticky flag. Without this,
    // a user who previously clicked Clear keeps hasBeenCleared=true through
    // the next real-submit training, which makes TrainingStatusCard's
    // `isActive` evaluate false and freezes Completion % at 0 the entire
    // run (background bar stays empty, Round counter still updates because
    // it doesn't gate on isActive). Mock playback path already does this
    // reset at execute/index.jsx:1490; this brings the real-submit path in
    // line.
    setHasBeenCleared(false);
    clearError('trainingExecution');
    
    try {
      // Use the current config from context
      const configToSend = currentConfig || config;
      
      // Determine the correct endpoint based on processing type
      const processingType = configToSend?.dataset_config?.processing_type || 'tabular';
      let endpoint;
      
      // dev-0.7.21 — canonical processing_type → endpoint:
      //   ALL real training paths (tabular + imaging_segmentation +
      //   imaging_classification) go through /execute_training_flower.
      //
      // Backend correction (Backend Agent, 2026-06-06): the legacy
      // /api/execute_segmentation endpoint is the dead M-seg.A demo
      // (unregistered in Flask dev-0.7.21 because Flask was never meant
      // to import torch). The actual imaging training surface is
      // /execute_training_flower — Flask reads session config, builds
      // Flower overrides via execution_overrides.build_seg_overrides()
      // / build_cls_overrides() for imaging, then subprocess-launches
      // `flwr run` against SuperLink → ClientApp pod (torch + GPU live
      // there, not in Flask). ProstateX-SEG Stage 1 was verified
      // end-to-end on dev-0.7.21 (12/12 PASS, backend commit 180c358).
      //
      // Before dev-0.7.21, switching imaging_segmentation to
      // /execute_segmentation routed real-submit attempts at a 404
      // endpoint; FE caught the error in execute/index.jsx's try/catch
      // and silently fell back to mock playback. Symptom: user clicks
      // Start with a ProstateX seg config and sees the fake-stream
      // demo instead of a real federated run.
      endpoint = '/execute_training_flower';

      // /execute_training_flower expects an empty body for app_config /
      // dataset_config — backend reads those from session state set by
      // the most recent POST /api/config call (per dev-0.7.6 contract).
      //
      // BUT dev-0.7.50 added persistence of `sites[]` into
      // training_runs.config_data via `data.get('sites') or []` (see
      // backend training_endpoints.py:691, 1071). `data` is the request
      // body — NOT the session config — so an empty body causes sites to
      // land as `[]` in DB regardless of what was saved to session via
      // /api/config. Verified twice on portal: run 188 + 189, both with
      // 2 sites picked at /configs, both DB rows had `sites: []`.
      //
      // Forward the saved currentConfig.sites here so backend's read
      // hits a populated array. Keeps the rest of the contract intact
      // (app_config / dataset_config still come from session).
      const savedSites = configToSend?.sites;
      const body = Array.isArray(savedSites) && savedSites.length > 0
        ? { sites: savedSites }
        : {};
      const response = await apiPost(endpoint, body);

      // All three paths return a task_id we poll via /flower_tasks/{id}.
      if (response && response.task_id) {
        flowerTaskIdRef.current = response.task_id;
      }
      
      // After successful training execution, refresh training summary to update dashboard
      // After successful training execution, refresh training summary to update dashboard
      
      return response;
    } catch (error) {
      console.error('[AppContext] Training execution failed:', error);
      handleApiError('trainingExecution', error);
      setIsRunning(false);
      throw error;
    }
  };

  const fetchFlowerTaskStatus = async () => {
    const taskId = flowerTaskIdRef.current;
    if (!taskId) return null;
    try {
      const task = await apiGet(`/flower_tasks/${taskId}`);

      const raw = task?.status || 'UNKNOWN';
      let mapped = normalizeTrainingStatus(raw);

      const backendProgress = task?.progress || {};
      const configuredTotalRounds = currentConfig?.app_config?.["num-server-rounds"] || 0;

      const totalRounds = backendProgress.total_rounds || configuredTotalRounds || 0;
      let currentRound = backendProgress.current_round || (mapped === 'completed' ? totalRounds : 0);

      const roundMetrics = task?.round_metrics || task?.metrics?.rounds;

      // Special handling for 'SUCCEEDED' status with missing metrics (race condition fix)
      // If the backend says "SUCCEEDED" but metrics aren't there yet, wait and refetch.
      if ((raw === 'SUCCEEDED' || mapped === 'completed') && (!roundMetrics || Object.keys(roundMetrics).length === 0)) {
        
        let retryCount = 0;
        const maxRetries = 3;
        let pTask = task;
        
        while (retryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s
          retryCount++;
          
          try {
             const retriedTask = await apiGet(`/flower_tasks/${taskId}`);
             
             // Check if we got metrics now
             const retriedMetrics = retriedTask?.round_metrics || retriedTask?.metrics?.rounds;
             if (retriedMetrics && Object.keys(retriedMetrics).length > 0) {
               return fetchFlowerTaskStatus(); 
             }
             
             if (retryCount === maxRetries) {
                console.log('[AppContext] Max retries reached. Metrics still missing despite SUCCEEDED status.');
             }
          } catch (retryError) {
             console.error('[AppContext] Error during retry fetch:', retryError);
          }
        }
      }

      // If the backend reports "UNKNOWN" (which maps to "running") or "running",
      // but we have metrics for rounds that are not yet reflected in the progress,
      // use the metrics to update the current round.
      if (roundMetrics && Object.keys(roundMetrics).length > 0) {
        const numericRounds = Object.keys(roundMetrics)
          .map(r => parseInt(r, 10))
          .filter(r => Number.isFinite(r))
          .sort((a, b) => a - b);
        
        if (numericRounds.length > 0) {
          const maxRound = numericRounds[numericRounds.length - 1];
          // If we have metrics for a later round than the backend reports, use that
          if (maxRound > currentRound) {
            currentRound = maxRound;
          }
        }
      }

      // Percentage source (dev-0.7.16 fix):
      //   - SUCCEEDED → 100 always (defensive: backend may publish final
      //     percentage one tick late).
      //   - Otherwise: TRUST backend's progress.percentage when present.
      //     Backend confirmed dev-0.7.16 publishes current_round=N together
      //     with percentage=N/total*100 (where current_round=N means "N
      //     rounds DONE"), so the value is authoritative and we don't need
      //     to recompute it FE-side.
      //   - Legacy fallback (kept for safety, only fires when backend
      //     somehow omits percentage): pre-dev-0.7.16 formula using
      //     max(round_metrics keys count, current_round - 1) / total.
      //     The (current_round - 1) term assumes the OLD convention where
      //     current_round=N meant "round N running, N-1 done"; on
      //     dev-0.7.16+ backend it would under-count by 1, but it only
      //     fires when backend's percentage is missing entirely.
      const roundMetricsKeyCount =
        roundMetrics && typeof roundMetrics === 'object'
          ? Object.keys(roundMetrics).length
          : 0;
      let percentage;
      if (mapped === 'completed') {
        percentage = 100;
      } else if (typeof backendProgress.percentage === 'number'
                 && Number.isFinite(backendProgress.percentage)) {
        percentage = Math.min(100, Math.max(0, backendProgress.percentage));
      } else if (totalRounds > 0) {
        const completedFromMetrics = roundMetricsKeyCount;
        const completedFromCurrentRound = Math.max(0, (currentRound || 0) - 1);
        const completed = Math.max(completedFromMetrics, completedFromCurrentRound);
        percentage = Math.min(100, (completed / totalRounds) * 100);
      } else {
        percentage = 0;
      }

      // REMOVED: Do not auto-complete based on rounds.
      // We MUST wait for the backend to explicitly report 'SUCCEEDED',
      // because that is when the final metrics are attached.
      // If we stop polling early (by setting mapped='completed'), we miss the metrics.
      /*
      if (mapped === 'running' && totalRounds > 0 && currentRound >= totalRounds) {
        console.log('[AppContext] Auto-completing task based on derived progress');
        mapped = 'completed';
        percentage = 100;
      }
      */



      // Prepare a latest_metrics payload so Execute can log the final round's
      // loss/accuracy when results become available.
      let latestMetrics = null;
      if (roundMetrics && Object.keys(roundMetrics).length > 0) {
        const numericRounds = Object.keys(roundMetrics)
          .map(r => parseInt(r, 10))
          .filter(r => Number.isFinite(r))
          .sort((a, b) => a - b);
        const lastRound = numericRounds[numericRounds.length - 1];
        const lastMetrics = roundMetrics[lastRound] || {};
        latestMetrics = {
          round_metrics: {
            round: lastMetrics.round ?? lastRound,
            loss: lastMetrics.loss,
            accuracy: lastMetrics.accuracy,
          },
        };
      }


      setTrainingStatus({
        status: mapped,
        server: mapped,
        training_type: 'tabular',
        flower_task: task
      });

      setTrainingProgress({
        status: mapped,
        training_type: 'tabular',
        progress: {
          current_round: currentRound,
          total_rounds: totalRounds,
          percentage
        },
        latest_metrics: latestMetrics
      });

      // Seg-run UI mirror — TrainingProgressCard + TrainingStatusCard read
      // segmentationProgress?.percentage / segmentationStatus for imaging
      // segmentation runs (see TrainingProgressCard.jsx:89-90, 97-98), but
      // the only state-updating poll for new-style seg runs (which go
      // through /api/execute_training_flower → fetchFlowerTaskStatus) is
      // *this* function — and historically it only wrote trainingStatus +
      // trainingProgress, never the segmentation* slots. Result: progress
      // bar / status banner froze at 0% / 'Ready' through the whole run.
      // Fall-back paths (fetchTrainingStatus's `training_type ===
      // 'segmentation'` branch + fetchCurrentSegmentationRun) are dead code
      // during active polling due to the unconditional `return task` at
      // execute/index.jsx:927. So we mirror here.
      if (currentConfig?.dataset_config?.processing_type === 'imaging_segmentation') {
        setSegmentationProgress({
          percentage,
          current_epoch: currentRound,
          total_epochs: totalRounds,
          message: null,
        });
        setSegmentationStatus(mapped);
      }

      // If the backend exposed real per-round metrics from the Flower log,
      // store them as trainingResults for the Execute page to visualize.
      if (roundMetrics && Object.keys(roundMetrics).length > 0) {
        console.log('[AppContext] round_metrics found on task, keys:', Object.keys(roundMetrics));
        setTrainingResults(roundMetrics);
      } else {
        if (mapped === 'completed') {
          try {
            const fallbackMetrics = await apiGet('/metrics/rounds');
            if (fallbackMetrics && fallbackMetrics.rounds && Object.keys(fallbackMetrics.rounds).length > 0) {
              setTrainingResults(fallbackMetrics.rounds);
            }
          } catch (metricsError) {
            console.warn('[AppContext] Failed to fetch fallback metrics:', metricsError);
          }
        }
      }

      if (mapped === 'running') {
        setIsRunning(true);
        setIsPaused(false);
        setIsCompleted(false);
      } else if (mapped === 'completed') {
        setIsRunning(false);
        setIsPaused(false);
        setIsCompleted(true);
      } else if (mapped === 'error') {
        setIsRunning(false);
        setIsPaused(false);
        setIsCompleted(false);
      }

      return task;
    } catch (error) {
      console.error('[AppContext] Flower task status error:', error);
      handleApiError('flowerTaskStatus', error);
      return null;
    }
  };

  const cancelFlowerTask = async () => {
    const taskId = flowerTaskIdRef.current;
    if (!taskId) return;
    try {
      await apiPost(`/flower_tasks/${taskId}/cancel`, {});
    } catch (error) {
      handleApiError('flowerTaskCancel', error);
      throw error;
    }
  };

  const fetchFlowerTaskLog = async (lines = 200) => {
    const taskId = flowerTaskIdRef.current;
    if (!taskId) return '';
    try {
      const text = await apiPost(`/flower_tasks/${taskId}/log?lines=${lines}`, null, 'GET', 'text');
      return text;
    } catch (error) {
      console.error('[AppContext] Flower task log error:', error);
      handleApiError('flowerTaskLog', error);
      return '';
    }
  };

  const pauseTraining = async () => {
    try {
      await apiPost('/pause_training');
      setIsPaused(true);
    } catch (error) {
      handleApiError('pauseTraining', error);
      throw error;
    }
  };

  const stopTraining = async () => {
    try {
      const isTabular = currentConfig?.dataset_config?.processing_type === 'tabular';
      if (isTabular && flowerTaskIdRef.current) {
        await cancelFlowerTask();
      } else {
        await apiPost('/stop_training');
      }
      setIsRunning(false);
      setIsPaused(false);
      setIsCompleted(false);
      
      // Clear training progress to prevent stale data from showing
      setTrainingProgress(null);
      setTrainingStatus(null);
    } catch (error) {
      handleApiError('stopTraining', error);
      throw error;
    }
  };

  const clearLogs = () => {
    // This function is used by Execute component for clearing logs
    // Implementation handled in Execute component
  };

  // Segmentation API functions
  const executeSegmentation = async (segmentationConfig = null) => {
    console.log('[APP_SEG] executeSegmentation called');
    console.log('[APP_SEG] Auth state:', { 
      isAuthenticated: auth?.isAuthenticated, 
      hasRefreshToken: !!auth?.refreshToken 
    });
    console.log('[APP_SEG] Current config:', config);
    
    setSegmentationLoading(true);
    clearError('segmentationExecution');
    
    try {
      // Use provided config or build from current context
      const configToSend = segmentationConfig || {
        model_type: config?.dataset_config?.model_type || 'unet',
        dataset_path: config?.dataset_config?.dataset_path || 'segmentation_data',
        'num-server-rounds': config?.app_config?.['num-server-rounds'] || 3,
        num_clients: config?.federation_config?.num_clients || config?.app_config?.num_clients || 2,
        segmentation_config: config?.dataset_config?.segmentation_config || {
          in_channels: 1,
          n_classes: 1,
          partitioning_method: 'iid'
        }
      };
      
      console.log('[APP_SEG] Executing segmentation with config:', configToSend);
      console.log('[APP_SEG] Config keys:', Object.keys(configToSend));
      console.log('[APP_SEG] About to call apiPost to /execute_segmentation...');
      
      const response = await apiPost('/execute_segmentation', configToSend);
      
      console.log('[APP_SEG] Segmentation API response received');
      console.log('[APP_SEG] Response:', response);
      console.log('[APP_SEG] Response type:', typeof response);
      console.log('[APP_SEG] Response keys:', response ? Object.keys(response) : 'null/undefined');
      
      if (response && response.success) {
        console.log('[APP_SEG] Training started successfully, run_id:', response.run_id);
        console.log('[APP_SEG] Response message:', response.message);
      } else {
        console.log('[APP_SEG] Unexpected response structure or failure:', response);
        console.log('[APP_SEG] Response success field:', response?.success);
        console.log('[APP_SEG] Response error field:', response?.error);
      }
      
      // Set status to running immediately after successful API call
      setSegmentationStatus('running');
      console.log('[APP_SEG] Segmentation status set to: running');
      
      // CRITICAL: Optimistically set isRunning to provide immediate UI feedback
      // Backend polling will confirm/maintain this state
      setIsRunning(true);
      setIsPaused(false);
      setIsCompleted(false);
      console.log('[APP_SEG] Set isRunning=true for immediate UI update');
      
      // Force a state update to ensure UI reflects the change
      setTimeout(() => {
        console.log('[APP_SEG] Verifying segmentation status after timeout...');
        setSegmentationStatus('running');
      }, 100);
      
      if (response && response.run_id) {
        console.log('[APP_SEG] Setting currentSegmentationRun to:', response.run_id);
        setCurrentSegmentationRun(response.run_id);
        // Add to history
        setSegmentationHistory(prev => [{
          id: response.run_id,
          timestamp: new Date().toISOString(),
          config: configToSend,
          status: 'running'
        }, ...prev]);
      } else {
        console.log('[APP_SEG] No run_id in response, creating temporary run for tracking');
        // Create a temporary run for tracking
        const tempRunId = `temp-${Date.now()}`;
        setCurrentSegmentationRun({ run_id: tempRunId });
        setSegmentationHistory(prev => [{
          id: tempRunId,
          timestamp: new Date().toISOString(),
          config: configToSend,
          status: 'running'
        }, ...prev]);
      }
      
      console.log('[APP_SEG] Returning response to caller:', response);
      return response; // Return the actual response, not wrapped in { success: true, data: response }
    } catch (error) {
      console.error('[APP_SEG] Segmentation execution failed:', error);
      console.log('[APP_SEG] Error details:', {
        message: error.message,
        status: error.status,
        stack: error.stack
      });
      handleApiError('segmentationExecution', error);
      return { success: false, error: error.message };
    } finally {
      setSegmentationLoading(false);
    }
  };

  const fetchSegmentationStatus = async (runId = null) => {
    try {
    const targetRunId = runId || currentSegmentationRun;
    if (!targetRunId) return null;
    
    try {
      const response = await apiGet(`/seg/${targetRunId}/status`);
      setSegmentationStatus(response);
      return response;
    } catch (error) {
      handleApiError('segmentationStatus', error);
    }
    
    return { success: true, data: response };
  } catch (error) {
    console.error('[AppContext] Segmentation execution failed:', error);
    handleApiError('segmentationExecution', error);
    return { success: false, error: error.message };
  } finally {
    setSegmentationLoading(false);
  }
};


const fetchCurrentSegmentationRun = async (retryOnAuth = true) => {
  console.log('[APP_FETCH] fetchCurrentSegmentationRun called');
  console.log('[APP_FETCH] retryOnAuth:', retryOnAuth);
  console.log('[APP_FETCH] Auth state:', { 
    isAuthenticated: auth?.isAuthenticated, 
    hasRefreshToken: !!auth?.refreshToken 
  });
  
  try {
    console.log('[APP_FETCH] Calling apiGet(/seg/current)...');
    const response = await apiGet('/seg/current');
    
    console.log('[APP_FETCH] fetchCurrentSegmentationRun response received');
    console.log('[APP_FETCH] Response:', response);
    console.log('[APP_FETCH] Response type:', typeof response);
    console.log('[APP_FETCH] Response keys:', response ? Object.keys(response) : 'null/undefined');
    
    if (response && response.status) {
      console.log('[APP_FETCH] Response status:', response.status);
      console.log('[APP_FETCH] Response has metrics:', !!response.metrics);
      console.log('[APP_FETCH] Response has summary:', !!response.summary);
      console.log('[APP_FETCH] Response has progress:', !!response.progress);
    }
    
    // Store the full response object for metrics extraction
    console.log('[APP_FETCH] About to call setCurrentSegmentationRun with:', response);
    console.log('[APP_FETCH] Response has summary?', !!response?.summary);
    console.log('[APP_FETCH] Response has summary.metrics?', !!response?.summary?.metrics);
    
    setCurrentSegmentationRun(response);
    
    console.log('[APP_FETCH] setCurrentSegmentationRun called successfully');
    console.log('[APP_FETCH] Stored full segmentationCurrentRun object with keys:', response ? Object.keys(response) : 'null');
    
    // Set only the status string, not the entire response
    if (response && response.status) {
      setSegmentationStatus(response.status);
      console.log('[APP_FETCH] Set segmentationStatus to:', response.status);
    } else {
      console.log('[APP_FETCH] No status in response, keeping current status');
    }
    
    return response;
  } catch (error) {
    console.error('[APP_FETCH] fetchCurrentSegmentationRun error:', error);
    console.log('[APP_FETCH] Error details:', {
      message: error.message,
      status: error.status,
      stack: error.stack
    });
    
    if (error.status === 401 && retryOnAuth && auth && auth.refreshToken) {
      console.log('[APP_FETCH] 401 error, refreshing token and retrying...');
      try {
        await auth.refreshToken();
        console.log('[APP_FETCH] Token refresh successful, retrying request');
        return await fetchCurrentSegmentationRun(false);
      } catch (refreshError) {
        console.error('[APP_FETCH] Token refresh failed:', refreshError);
      }
    }
    
    // 404 is expected when no run is active
    if (error.status !== 404) {
      handleApiError('currentSegmentationRun', error);
    }
    
    console.log('[APP_FETCH] Setting currentSegmentationRun to null due to error');
    setCurrentSegmentationRun(null);
    return null;
  }
};

  const fetchSegmentationVisualizations = async (vizTag, round = null, epoch = null, limit = 50, offset = 0, retryOnAuth = true) => {
    console.log('[APP_VIZ] fetchSegmentationVisualizations called with params:', {
      vizTag, round, epoch, limit, offset, retryOnAuth
    });
    console.log('[APP_VIZ] Auth state:', { 
      isAuthenticated: auth?.isAuthenticated, 
      hasRefreshToken: !!auth?.refreshToken,
      hasGetToken: typeof auth?.getToken === 'function',
      tokenLength: auth?.getToken ? (auth.getToken()?.length || 0) : 0
    });
    
    setSegmentationVisualizationsLoading(true);
    clearError('segmentationVisualizations');
    
    try {
      // Ensure user is authenticated before making request
      if (!auth || !auth.isAuthenticated) {
        console.error('[APP_VIZ] User not authenticated, cannot fetch visualizations');
        throw new Error('User not authenticated');
      }
      
      // Ensure auth context is set in API utility
      console.log('[APP_VIZ] Re-setting auth context in API utility before request');
      setAuthContext(auth);
      
      // Check token availability
      const currentToken = auth.getToken ? auth.getToken() : null;
      console.log('[APP_VIZ] Current token status:', {
        hasToken: !!currentToken,
        tokenLength: currentToken?.length || 0,
        tokenPreview: currentToken ? currentToken.substring(0, 20) + '...' : 'none'
      });
      
      // Refresh token proactively before making the request
      if (auth && auth.refreshToken) {
        try {
          console.log('[APP_VIZ] Refreshing token before visualization request');
          await auth.refreshToken();
          const refreshedToken = auth.getToken ? auth.getToken() : null;
          console.log('[APP_VIZ] Token refreshed successfully:', {
            hasRefreshedToken: !!refreshedToken,
            refreshedTokenLength: refreshedToken?.length || 0
          });
        } catch (refreshError) {
          console.warn('[APP_VIZ] Token refresh failed before visualization request:', refreshError);
        }
      }
      
      const params = new URLSearchParams({
        viz_tag: vizTag,
        limit: limit.toString(),
        offset: offset.toString()
      });
      
      if (round !== null) params.append('round', round.toString());
      if (epoch !== null) params.append('epoch', epoch.toString());
      
      const endpoint = `/seg/visualizations?${params}`;
      console.log('[APP_VIZ] Making API request to:', endpoint);
      console.log('[APP_VIZ] Full URL will be:', `${API_BASE}${endpoint}`);
      
      const response = await apiGet(endpoint);
      console.log('[APP_VIZ] Visualization API response received:', response);
      console.log('[APP_VIZ] Images found:', response?.images?.length || 0);
      
      setSegmentationVisualizations(response.images || []);
      return response;
    } catch (error) {
      console.error('[APP_VIZ] Visualization fetch error:', error);
      console.log('[APP_VIZ] Error details:', {
        message: error.message,
        status: error.status,
        stack: error.stack
      });
      
      // Handle token expiration with automatic refresh
      if (error.status === 401 && retryOnAuth && auth && auth.refreshToken) {
        console.log('[APP_VIZ] Token expired during visualization fetch, attempting refresh...');
        try {
          await auth.refreshToken();
          console.log('[APP_VIZ] Token refreshed successfully, retrying visualization request...');
          // Retry the request once after token refresh
          return await fetchSegmentationVisualizations(vizTag, round, epoch, limit, offset, false);
        } catch (refreshError) {
          console.error('[AppContext] Token refresh failed during visualization fetch:', refreshError);
          handleApiError('segmentationVisualizations', refreshError);
          return { images: [], count: 0, total: 0 };
        }
      }
      
      handleApiError('segmentationVisualizations', error);
      return { images: [], count: 0, total: 0 };
    } finally {
      setSegmentationVisualizationsLoading(false);
    }
  };

  // Stream segmentation events (for real-time updates)
  const streamSegmentationEvents = (runId, onEvent) => {
    // NOTE: API_BASE already ends in `/api` — do NOT prepend another `/api`
    // (that produced the historical `/api/api/seg/...` double-prefix bug).
    // SSE stays gated off this round (segmentation uses polling /seg/current);
    // this URL is corrected for when SSE is enabled later.
    const eventSource = new EventSource(`${API_BASE}/seg/${runId}/events`);
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onEvent(data);
        
        // Update progress state
        if (data.type === 'progress') {
          setSegmentationProgress(data.data);
        }
      } catch (error) {
        console.error('Error parsing segmentation event:', error);
      }
    };
    
    eventSource.onerror = (error) => {
      console.error('Segmentation event stream error:', error);
      eventSource.close();
    };
    
    return eventSource;
  };

  // Fetch all data at once
  const fetchAllData = async () => {
    await Promise.all([
      fetchModels(),
      fetchDatasets(),
      fetchTrainingStatus(),
      fetchConfig(),
      fetchDisplayableFeatures(),
      fetchSelectedFeatures(),
      fetchTrainingDefaultsFromServer()
    ]);
  };

  // Refresh specific data
  const refreshModels = () => fetchModels();
  const refreshDatasets = () => fetchDatasets();
  const refreshTrainingStatus = () => {
    const processingType =
      currentConfig?.dataset_config?.processing_type ||
      config?.dataset_config?.processing_type ||
      'tabular';
    const isTabular = processingType === 'tabular';
    if (isTabular && flowerTaskIdRef.current) {
      return fetchFlowerTaskStatus();
    }
    return fetchTrainingStatus();
  };
  const refreshTrainingProgress = () => {
    const processingType =
      currentConfig?.dataset_config?.processing_type ||
      config?.dataset_config?.processing_type ||
      'tabular';
    const isTabular = processingType === 'tabular';
    if (isTabular && flowerTaskIdRef.current) {
      return fetchFlowerTaskStatus();
    }
    return fetchTrainingProgress();
  };
  const refreshConfig = () => fetchConfig();
  const refreshAll = () => fetchAllData();

  // Fetch all data on component mount
  useEffect(() => {
    if (auth?.isAuthenticated && !auth?.loading) {
      console.log('Auth is ready, fetching all data...');
      fetchAllData();
      // Also fetch training summary for dashboard
      fetchTrainingSummary();
    }
  }, [auth?.isAuthenticated, auth?.loading]);

  // --- Phase F1: site loaders (mock-backed by default) ---
  // Backend endpoints (GET /sites, /sites/{id}/capabilities, /me/sites) are not
  // implemented yet. With VITE_SITES_USE_MOCK="false" the loaders will try the
  // real endpoints and surface the 404 — useful only for testing the live cutover.
  const fetchSites = useCallback(async () => {
    setSitesLoading(true);
    setSitesError(null);
    try {
      if (SITES_USE_MOCK) {
        setSites(mockSites);
        const capsBySite = {};
        for (const site of mockSites) {
          capsBySite[site.site_id] = mockCapabilitiesForSite(site.site_id);
        }
        setSiteCapabilities(capsBySite);
        return mockSites;
      }
      const data = await apiGet('/sites');
      const rawList = Array.isArray(data) ? data : (data?.data || []);
      // Demo-only rename for site_smh_01 — frontend overrides display strings
      // only; site_id / capability_id / dataset_ref / backend identity remain
      // unchanged. Mirrored in src/utils/schemaValidation.js and
      // src/utils/featureDistribution.js so SMH never surfaces by its real
      // name in any FE-rendered surface, regardless of which response carries
      // it. Reason: real St. Michael's Hospital operates its own federated
      // learning platform; avoid implying any association during demo.
      const list = rawList.map((s) =>
        s?.site_id === 'site_smh_01'
          ? {
              ...s,
              display_name: 'South Metro Hospital',
              organization_display_name: 'South Metro Health',
            }
          : s
      );
      setSites(list);
      // F1.8 — AKS-real backfill: /api/datasets is NOT exposed by AKS DEV
      // (dev-0.7.x). The source of truth for "what logical datasets exist"
      // is the per-site /sites/{id}/capabilities response. Fan out in
      // parallel, flatten, and write into availableDatasets so the
      // /datasets page and Dashboard tile can render via
      // groupCapabilitiesByDatasetRef(availableDatasets, sites). Mock branch
      // already populates capabilities above.
      let flatCaps = [];
      try {
        const capsArrays = await Promise.all(
          list.map((s) => fetchSiteCapabilities(s.site_id))
        );
        flatCaps = capsArrays.flat();
      } catch (capsErr) {
        console.warn('[AppContext] one or more fetchSiteCapabilities failed:', capsErr);
      }
      if (flatCaps.length > 0) {
        setAvailableDatasets(flatCaps);
        setDatasets(flatCaps.map((c) => c.capability_id).filter(Boolean));
      }
      return list;
    } catch (err) {
      console.error('[AppContext] fetchSites failed:', err);
      setSitesError(err.message);
      return [];
    } finally {
      setSitesLoading(false);
    }
  }, []);

  const fetchSiteCapabilities = useCallback(async (siteId) => {
    if (!siteId) return [];
    try {
      if (SITES_USE_MOCK) {
        const caps = mockCapabilitiesForSite(siteId);
        setSiteCapabilities(prev => ({ ...prev, [siteId]: caps }));
        return caps;
      }
      const data = await apiGet(`/sites/${encodeURIComponent(siteId)}/capabilities`);
      const list = Array.isArray(data) ? data : (data?.data || []);
      setSiteCapabilities(prev => ({ ...prev, [siteId]: list }));
      return list;
    } catch (err) {
      console.error('[AppContext] fetchSiteCapabilities failed:', err);
      return [];
    }
  }, []);

  // Auto-populate sites once when authenticated (mock returns instantly; real
  // call would be a single GET). Safe no-op if sites already populated.
  useEffect(() => {
    if (auth?.isAuthenticated && !auth?.loading && sites.length === 0) {
      fetchSites();
    }
  }, [auth?.isAuthenticated, auth?.loading, sites.length, fetchSites]);

  // Context value object
  const contextValue = useMemo(() => ({
    // Auth - Pass through the stable auth object
    auth,

    // Shared state
    models,
    datasets,
    trainingStatus,
    trainingProgress,
    config,
    currentModel,
    currentConfig,
    availableModels,
    availableDatasets,
    displayableFeatures,
    displayableFeaturesLoading,
    selectedFeatures,
    featureMetadata,
    setFeatureMetadata,
    perDatasetFeaturesMap,
    setPerDatasetFeaturesMap,
    featureVisualizations,
    executionHistory,
    
    // Segmentation state
    segmentationStatus,
    segmentationProgress,
    segmentationVisualizations,
    segmentationHistory,
    currentSegmentationRun,
    
    // Loading states
    modelsLoading,
    datasetsLoading,
    statusLoading,
    configLoading,
    progressLoading,
    executionHistoryLoading,
    configSaving,
    setIsCompleted, 
    isCompleted,
    isRunning,
    setIsRunning,
    isPaused,
    setIsPaused,
    segmentationLoading,
    segmentationVisualizationsLoading,
    isClearing,
    
    // Execute page specific states
    progress,
    setProgress,
    currentRound,
    setCurrentRound,
    totalRounds,
    setTotalRounds,
    connectedClients,
    setConnectedClients,
    visualizationsGenerated,
    setVisualizationsGenerated,
    hasBeenCleared,
    setHasBeenCleared,
    isPolling,
    setIsPolling,
    error,
    setError,
    isGeneratingVisualizations,
    setIsGeneratingVisualizations,
    logs,
    setLogs,
    trainingResults,
    setTrainingResults,
    
    // Error states
    errors,
    clearError,
    
    // API functions
    fetchModels,
    fetchDatasets,
    fetchTrainingStatus,
    fetchTrainingProgress,
    fetchConfig,
    saveConfig,
    fetchAllData,

    // Per-user training defaults (Settings → Federated Training)
    trainingDefaults,
    fetchTrainingDefaultsFromServer,
    applyTrainingDefaults,
    refreshModels,
    refreshDatasets,
    refreshTrainingStatus,
    refreshTrainingProgress,
    refreshConfig,
    refreshAll,
    fetchSelectedFeatures,
    updateSelectedFeatures,
    fetchDisplayableFeatures,
    generateFeatureVisualizations,
    clearCachedData,
    clearAllTrainingData,
    clearTrainingRunHistory,
    isTrainingCompleted,
    
    // Training database functions
    fetchTrainingRuns,
    fetchTrainingSummary,
    fetchTrainingMetrics,
    trainingRuns,
    trainingSummary,
    trainingDataLoading,
    
    // Training execution functions
    executeTraining,
    pauseTraining,
    stopTraining,
    clearLogs,
    fetchFlowerTaskStatus,
    cancelFlowerTask,
    fetchFlowerTaskLog,
    
    // Segmentation API functions
    executeSegmentation,
    fetchSegmentationStatus,
    fetchCurrentSegmentationRun,
    fetchSegmentationVisualizations,
    streamSegmentationEvents,
    
    // Setters for components that need to update state
    setModels,
    setDatasets,
    setTrainingStatus,
    setTrainingProgress,
    setConfig,
    setCurrentModel,
    setAvailableModels,
    setAvailableDatasets,
    setExecutionHistory,
    setCurrentConfig, // Add setCurrentConfig to context value
    setSelectedFeatures,
    setFeatureVisualizations,
    setSegmentationStatus,
    setSegmentationProgress,
    setSegmentationVisualizations,
    setSegmentationHistory,
    setSegmentationVisualizationsLoading,
    setCurrentSegmentationRun,

    // --- Phase F1: site-based selection ---
    sites,
    siteCapabilities,
    selectedSites,
    sitesLoading,
    sitesError,
    setSites,
    setSelectedSites,
    fetchSites,
    fetchSiteCapabilities,

    // --- Phase F1.x prototype: schema validation state ---
    schemaPreCheck,
    setSchemaPreCheck,
  }), [
    // Auth
    auth,
    // Shared state
    models, datasets, trainingStatus, trainingProgress, config,
    currentModel, currentConfig, availableModels, availableDatasets,
    displayableFeatures, displayableFeaturesLoading, selectedFeatures, featureVisualizations,
    featureMetadata,
    executionHistory,
    // Segmentation state
    segmentationStatus, segmentationProgress, segmentationVisualizations,
    segmentationHistory, currentSegmentationRun,
    // Loading states
    modelsLoading, datasetsLoading, statusLoading, configLoading,
    progressLoading, executionHistoryLoading, configSaving,
    isCompleted, isRunning, isPaused, segmentationLoading, segmentationVisualizationsLoading, isClearing,
    // Execute page specific states
    progress, currentRound, totalRounds, connectedClients, visualizationsGenerated,
    hasBeenCleared, isPolling, error, isGeneratingVisualizations, logs, trainingResults,
    errors,
    // Training data loading
    trainingRuns, trainingSummary, trainingDataLoading,
    // Memoize functions that are created inside component but not wrapped in useCallback
    // (Ideally all these functions should be wrapped in useCallback too, but that's a larger refactor)
    refreshModels, refreshDatasets, refreshTrainingStatus, refreshTrainingProgress,
    refreshConfig, refreshAll, clearLogs, isTrainingCompleted, 
    // Stable function references from useCallback
    fetchTrainingSummary,
    // F1: site-based selection
    sites, siteCapabilities, selectedSites, sitesLoading, sitesError,
    fetchSites, fetchSiteCapabilities,
    // Schema validation slice
    schemaPreCheck,
  ]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};
